<template>
    <form class="options" autocomplete="off">
        <table>
            <thead>
                <tr>
                    <th class="narrow"></th>
                    <th class="narrow">Default</th>
                    <th>Option Name</th>
                    <th v-show="showAdvanced">Export Value</th>
                    <th class="narrow toggles">
                        <button
                            type="button"
                            title="Show advanced options"
                            :class="{ active: showAdvanced }"
                            @click="$emit('show-advanced', !showAdvanced)"
                        >
                            <i class="far fa-cog"></i>
                        </button>
                    </th>
                </tr>
            </thead>
            <draggable tag="tbody" handle=".handle" @sort="onSort">
                <tr class="option" v-for="(opt, index) in feature.options" :key="opt.value">
                    <td>
                        <i class="handle far fa-grip-horizontal"></i>
                    </td>
                    <td class="default">
                        <input
                            type="checkbox"
                            name="isDefault"
                            :checked="opt.isDefault"
                            @change="e => update(e, index)"
                        />
                    </td>
                    <td>
                        <input
                            type="text"
                            name="name"
                            class="wide"
                            :value="opt.name"
                            @blur="e => update(e, index)"
                        />
                    </td>
                    <td v-show="showAdvanced">
                        <input
                            type="text"
                            name="value"
                            class="wide"
                            :id="`opt-${index}`"
                            :value="opt.value"
                            @blur="e => update(e, index)"
                        />
                    </td>
                    <td class="actions">
                        <button @click="remove(index)" class="button small" type="button">
                            <i class="far fa-trash-alt"></i>
                        </button>
                    </td>
                </tr>
            </draggable>
            <tr class="add-option" @keypress.enter="() => {if(canAdd) add()}">
                <td></td>
                <td class="default">
                    <input type="checkbox" name="pendingIsDefault" v-model="pendingOpt.isDefault" />
                </td>
                <td>
                    <input ref="pending-name" type="text" class="wide" v-model="pendingOpt.name" />
                </td>
                <td v-show="showAdvanced">
                    <input type="text" class="wide" v-model="pendingOpt.value" />
                </td>
                <td class="actions">
                    <button :disabled="!canAdd" @click="add" class="button small" type="button">Add</button>
                </td>
            </tr>
        </table>
    </form>
</template>

<script>
    import draggable from 'vuedraggable';
    import cloneDeep from 'lodash/cloneDeep'
    export default {
        name: 'PropTabOptions',
        data() {
            return {
                pendingOpt: {
                    name: '',
                    value: '',
                    isDefault: false
                }
            };
        },
        components: {
            draggable
        },
        props: {
            feature: {
                required: true
            },
            showAdvanced: {
                type: Boolean,
                default: false
            }
        },
        computed: {
            canAdd() {
                return (
                    this.feature.options !== undefined &&
                    this.valueIsUnique(this.pendingOpt.value)
                );
            }
        },
        watch: {
            'pendingOpt.name'(newVal) {
                if (!this.showAdvanced) {
                    this.pendingOpt.value = newVal;
                }
            },
            feature() {
                this.resetPendingOpt();
            }
        },
        methods: {
            add() {
                let optsClone = cloneDeep(this.feature.options);
                if (this.pendingOpt.isDefault) {
                    optsClone.map(opt => opt.isDefault = false);
                }
                optsClone.push(this.pendingOpt);
                this.commitUpdate(optsClone);
                this.resetPendingOpt();
                this.$refs['pending-name'].focus();
            },
            remove(index) {
                let optsClone = cloneDeep(this.feature.options);
                optsClone.splice(index, 1);
                this.commitUpdate(optsClone);
            },
            onSort(e) {
                let optsClone = cloneDeep(this.feature.options);
                let opt = optsClone.splice(e.oldIndex, 1)[0];
                optsClone.splice(e.newIndex, 0, opt);
                this.commitUpdate(optsClone);
            },
            update(e, index) {
                let propName = e.target.getAttribute('name');
                let value = e.target.value;
                let optsClone = cloneDeep(this.feature.options);

                if ((propName === 'value' || (propName === 'name' && !this.showAdvanced))
                    && !this.valueIsUnique(value, index))
                {
                    this.$store.commit('tooltip/set', {
                        trigger: '#' + e.target.id,
                        message: 'Value must be unique',
                        class: 'error'
                    });

                    e.target.value = this.feature.options[index][propName] || '';
                    e.target.select();
                    return;
                }

                if (propName === 'isDefault') {
                    value = e.target.checked;
                    if (value) {
                        for (let opt of optsClone) {
                            opt.isDefault = false;
                        }
                    }
                }

                if (propName === 'name' && !this.showAdvanced) {
                    optsClone[index].value = value;
                }

                optsClone[index][propName] = value;
                this.commitUpdate(optsClone);
            },
            valueIsUnique(val, index = null) {
                let optsClone = cloneDeep(this.feature.options);
                if (index !== null) optsClone.splice(index, 1);
                return !optsClone.some(opt => String(opt.value) === String(val));
            },
            commitUpdate(options) {
                this.$store.commit('form/updateFeature', {
                    feature: this.feature,
                    updates: {
                        options
                    }
                });
            },
            resetPendingOpt() {
                this.pendingOpt = {
                    name: '',
                    value: '',
                    isDefault: false
                };
            }
        }
    };
</script>

<style lang="scss" scoped>
    .no-options {
        border: 2px dashed $color-border;
        padding: 1em 2em;
        text-align: center;
        color: $color-disabled-text;
        margin-bottom: 1em;
    }
    table {
        width: 100%;
    }

    input {
        &[type='text'] {
            width: 150;
        }
    }

    th {
        text-align: center;
        font-size: 14px;
    }

    td,
    th {
        padding: 0.25em;
    }

    td.default {
        text-align: center;
    }

    td.actions {
        text-align: right;
    }

    .option td,
    .add-option td {
        border-top: 1px dashed transparent;
        border-bottom: 1px dashed transparent;
    }

    .handle {
        cursor: grab;
    }

    .sortable-ghost {
        td {
            border-color: $color-highlight;
        }
    }

    .add-option {
        td {
            border-color: #ccc;

            input {
                opacity: 0.8;
            }
        }
    }

    .button.small {
        height: 24px;
        padding: 0 0.5em !important;
    }

    .wide {
        width: 100%;
    }

    .narrow {
        white-space: nowrap;
        width: 1%;
    }

    .toggles {
        padding-top: 0;
        padding-bottom: 0;
        text-align: right;

        button {
            background-color: transparent;
            border: none;
            padding: 4px;
            @include border-radius-small;

            &:hover {
                background-color: rgba(0, 0, 0, 0.07);
            }

            &.active {
                background-color: rgba(0, 0, 0, 0.13);
            }

            i {
                font-size: 15px;
            }
        }
    }
</style>
